// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * Collection query hook with mode routing.
 *
 * Delegates to either local RxDB (useLocalCollectionQuery) or remote D1
 * based on the CollectionModeProvider context.
 *
 * When no provider is mounted, all collections route to local (backward compatible).
 *
 * NOTE: The `queryBuilder` callback is a local-only concept (it receives an
 * RxCollection). When the collection is in remote mode, the hook fetches all
 * documents from the remote API without applying the queryBuilder.
 */

import { useCollectionMode } from '../CollectionModeContext';
import { useLocalCollectionQuery } from './useLocalCollectionQuery';
import { useRemoteQueryConditional } from './useRemoteQuery';

/**
 * React hook for querying a collection by name.
 *
 * Routes to local RxDB or remote D1 based on CollectionModeProvider context.
 * When no provider is mounted, all collections route to local.
 *
 * @param db - RxDatabase instance (or null/undefined)
 * @param collectionName - Name of the collection to query
 * @param queryBuilder - Optional function that receives the RxCollection and returns an RxQuery.
 *   Only used in local mode. In remote mode, all documents are fetched.
 * @returns Object with `data`, `isLoading`, and `error`
 *
 * @example
 * ```tsx
 * function ProjectSelect({ orgId }: { orgId: string }) {
 *   const { db } = useDatabase();
 *   const queryBuilder = useCallback(
 *     (collection: RxCollection) =>
 *       collection.find({ selector: { orgId, archived: false } }),
 *     [orgId]
 *   );
 *   const { data, isLoading } = useCollectionQuery(db, 'projects', queryBuilder);
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   return (
 *     <select>
 *       {data.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
 *     </select>
 *   );
 * }
 * ```
 */
export function useCollectionQuery<T = Record<string, unknown>>(
  db: any,
  collectionName: string,
  queryBuilder?: (collection: any) => any
): { data: T[]; isLoading: boolean; error: Error | null } {
  const modeCtx = useCollectionMode();
  const mode = modeCtx?.getMode(collectionName) ?? 'local';
  const isRemote = mode === 'remote';

  // Both hooks always called (rules of hooks), only one is active.
  const localResult = useLocalCollectionQuery<T>(
    isRemote ? null : db,
    collectionName,
    queryBuilder
  );

  const remoteResult = useRemoteQueryConditional<T>(
    isRemote ? collectionName : null
  );

  return isRemote ? remoteResult : localResult;
}
